import { inject, injectable } from 'inversify';

import { Authority, User } from '@project44/ui-utils';
import {
  DtoBase,
  SharedModels,
  TenantEnums,
  TenantModels,
  UserModels,
} from '@project44-models/iam-gateway';

import { UserPrincipalAdapter } from '../../../external/UserPrincipal/infrastructure/adapters/UserPrincipal.adapter';
import { UnauthorizedAccessException } from '../../../shared/core/application/errors';
import { TenantController } from '../../Tenant/infrastructure/controllers/Tenant.controller';
import { TenantSpecificTenantAdminRolesEnum } from '../../UnifiedRoles/domain/enums';
import { TUnifiedRoleCollectionDto } from '../../UnifiedRoles/domain/interfaces';
import {
  UserTenantAgnosticUnifiedRolesQuery,
  UserTenantAgnosticUnifiedRolesUpdateCommand,
  UserTenantSpecificTenantAdminPolicyUpdateCommand,
  UserTenantSpecificUnifiedRolesQuery,
  UserTenantSpecificUnifiedRolesUpdateCommand,
} from '../../User/application/useCase';
import { SpecificUserQuery } from '../application/useCase/SpecificUserQuery.useCase';
import { IUserCreationForm } from '../domain/interfaces';
import { UserFactory } from '../infrastructure/factories/User.factory';
import { UserCreatorCommand } from './useCase/UserCreatorCommand.useCase';

@injectable()
export class UserService {
  constructor(
    @inject(UserTenantAgnosticUnifiedRolesUpdateCommand)
    private readonly userTenantAgnosticUnifiedRolesUpdateCommand: UserTenantAgnosticUnifiedRolesUpdateCommand,

    @inject(UserTenantSpecificUnifiedRolesQuery)
    private readonly userTenantSpecificUnifiedRolesQuery: UserTenantSpecificUnifiedRolesQuery,

    @inject(UserTenantSpecificUnifiedRolesUpdateCommand)
    private readonly userTenantSpecificUnifiedRolesUpdateCommand: UserTenantSpecificUnifiedRolesUpdateCommand,

    @inject(UserTenantSpecificTenantAdminPolicyUpdateCommand)
    private readonly userTenantSpecificTenantAdminPolicyUpdateCommand: UserTenantSpecificTenantAdminPolicyUpdateCommand,

    @inject(UserTenantAgnosticUnifiedRolesQuery)
    private readonly userTenantAgnosticUnifiedRolesQuery: UserTenantAgnosticUnifiedRolesQuery,

    @inject(UserFactory) private readonly userFactory: UserFactory,

    @inject(UserCreatorCommand)
    private readonly userCreatorCommand: UserCreatorCommand,

    @inject(TenantController)
    private readonly tenantController: TenantController,

    @inject(SpecificUserQuery)
    private readonly specificUserQuery: SpecificUserQuery,

    @inject(UserPrincipalAdapter)
    private readonly userPrincipalAdapter: UserPrincipalAdapter,
  ) {}

  public async queryUserTenantAgnosticRoles(
    userPrincipal: User,
    payload: {
      user: DtoBase<UserModels.UserViewModel>;
      tenant: DtoBase<TenantModels.TenantViewModel>;
    },
  ): Promise<{ userTenantAgnosticUnifiedRoles: TUnifiedRoleCollectionDto }> {
    return this.userTenantAgnosticUnifiedRolesQuery.execute(
      userPrincipal,
      payload,
    );
  }

  public async updateUserTenantAgnosticRoles(
    userPrincipal: User,
    payload: {
      user: DtoBase<UserModels.UserViewModel>;
      tenant: DtoBase<TenantModels.TenantViewModel>;
      roles: DtoBase<SharedModels.UnifiedRoleCollectionViewModel>;
    },
  ): Promise<void> {
    const { user, tenant, roles } = payload;

    return this.userTenantAgnosticUnifiedRolesUpdateCommand.execute(
      userPrincipal,
      {
        user,
        tenant,
        unifiedRoles: roles,
      },
    );
  }

  public async queryUserTenantSpecificRoles(
    userPrincipal: User,
    payload: {
      user: DtoBase<UserModels.UserViewModel>;
      tenant: DtoBase<TenantModels.TenantViewModel>;
    },
  ): Promise<{ userTenantSpecificUnifiedRoles: TUnifiedRoleCollectionDto }> {
    return this.userTenantSpecificUnifiedRolesQuery.execute(
      userPrincipal,
      payload,
    );
  }

  public async updateUserTenantSpecificRoles(
    userPrincipal: User,
    payload: {
      user: DtoBase<UserModels.UserViewModel>;
      tenant: DtoBase<TenantModels.TenantViewModel>;
      roles: DtoBase<SharedModels.UnifiedRoleCollectionViewModel>;
    },
  ): Promise<void> {
    const { user, tenant, roles } = payload;
    return this.userTenantSpecificUnifiedRolesUpdateCommand.execute(
      userPrincipal,
      { user, tenant, unifiedRoles: roles },
    );
  }

  public async createUser(
    userPrincipal: User,
    payload: {
      userFormCreator: IUserCreationForm;
    },
  ): Promise<{ user: DtoBase<UserModels.UserViewModel> }> {
    const tenant = await this.getUserPrincipalTenant(userPrincipal);

    await this.userCreatorCommand.execute(userPrincipal, {
      userFormCreator: payload.userFormCreator,
      tenant,
    });

    const { user } = await this.getUserById(userPrincipal, {
      tenant,
      userId: payload.userFormCreator.username,
    });

    if (payload.userFormCreator.isAdmin === 'true') {
      await this.changeUserTenantSpecificAdminRolesType(userPrincipal, {
        user,
        tenant,
        tenantSpecificTenantAdminPolicy:
          TenantSpecificTenantAdminRolesEnum.WITH_TENANT_SPECIFIC_TENANT_ADMIN_ROLES,
      });
    }

    return {
      user,
    };
  }

  public async getUserById(
    userPrincipal: User,
    payload: {
      userId: string;
      tenant: DtoBase<TenantModels.TenantViewModel>;
      includeDeleted?: boolean;
    },
  ): Promise<{ user: DtoBase<UserModels.UserViewModel> }> {
    return this.specificUserQuery.execute(userPrincipal, payload);
  }

  public async changeUserTenantSpecificAdminRolesType(
    userPrincipal: User,
    payload: {
      user: DtoBase<UserModels.UserViewModel>;
      tenant: DtoBase<TenantModels.TenantViewModel>;
      tenantSpecificTenantAdminPolicy: TenantSpecificTenantAdminRolesEnum;
    },
  ) {
    const tenantSpecificTenantAdminPolicy =
      payload.tenantSpecificTenantAdminPolicy;

    const userTenantSpecificRolesHandler =
      this.userFactory.createUserUnifiedRolesTenantSpecificAggregate({
        userPrincipal,
        user: payload.user,
        tenant: payload.tenant,
      });

    return this.userTenantSpecificTenantAdminPolicyUpdateCommand.execute(
      userPrincipal,
      {
        tenantSpecificTenantAdminPolicy,
        userTenantSpecificRolesHandler,
      },
    );
  }

  public getUserPrincipalTenant = async (
    userPrincipal: User,
  ): Promise<DtoBase<TenantModels.TenantViewModel>> => {
    try {
      const { tenant } = await this.tenantController.getTenantByUuid(
        userPrincipal,
        {
          tenantStatus: TenantEnums.TenantStatusEnum.ACTIVE_STATUS,
          tenantUuid: userPrincipal.tenantUuid,
        },
      );
      return tenant;
    } catch (e) {
      if (
        e instanceof UnauthorizedAccessException &&
        userPrincipal.hasAnyOfPermissions([
          'SAVE_USER_ROLES' as unknown as Authority,
        ])
      ) {
        return new DtoBase<TenantModels.TenantViewModel>({
          model: this.userPrincipalAdapter.toTenant(userPrincipal),
        });
      }
      throw e;
    }
  };
}
